import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Flex,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  AlertBadge,
  IconButton,
} from "@bdo/cornerstone-ui";
import {
  RiGroupLine,
  RiEditLine,
  RiArchiveLine,
  RiCornerDownRightLine,
  RiSettings2Line,
  RiCheckboxCircleFill,
  RiArrowRightLongLine,
  RiErrorWarningLine,
  RiMoreLine,
} from "@remixicon/react";
import { parseRelativeDate } from "@/helpers";
import { usePrefetch } from "@/app/workflows/workflowInstancesApi";
import { useAppDispatch, useFeatureFlags } from "@/hooks";
import { openManageAccess } from "@/features/overlays/manageAccessSlice";
import type { WorkflowStepInstance } from "@/types";

interface WorkflowCardProps {
  id: string;
  title: string;
  workflowDefinitionName: string;
  stepInstance?: WorkflowStepInstance;
  isAwaitingAttention: boolean;
  modifiedOn: string;
  version: string;
}

export function WorkflowCard({
  id,
  title,
  workflowDefinitionName,
  stepInstance,
  isAwaitingAttention,
  modifiedOn,
  version,
}: Readonly<WorkflowCardProps>) {
  const { t } = useTranslation("translation", {
    keyPrefix: "pages.workflows.workflowCards.workflow",
  });

  const prefetch = usePrefetch("getWorkflowInstanceById");
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isManageAccessEnabled, isRenameEnabled, isArchiveEnabled } =
    useFeatureFlags();

  const hasAnyAction =
    isManageAccessEnabled || isRenameEnabled || isArchiveEnabled;

  function handlePrefetch() {
    prefetch({
      id,
      expand:
        "WorkflowStepInstances($expand=AdditionalInformation($expand=Attachments($select=id,fileName)),Outputs($expand=WorkflowStepOutputDefinition,Attachments($select=id,fileName))),WorkflowDefinition($expand=Steps)",
    });
  }

  return (
    <div
      className="workflow-card bg-white border border-shade-lighter hover:border-shade-light transition ease duration-200 p-lg w-full min-w-[320px] "
      title={title}
      data-testid={`workflow-card-${id}`}
    >
      <Flex justify="between">
        <h2
          className="text-lg font-bold text-mono-darker text-ellipsis overflow-hidden whitespace-nowrap"
          data-testid={`workflow-card-title-${id}`}
        >
          {title}
        </h2>
        {hasAnyAction && (
          <Dropdown data-testid={`workflow-card-dropdown-${id}`}>
            <IconButton
              icon={<RiMoreLine />}
              aria-label={t("dropdown.triggerButtonAriaLabel")}
              data-testid={`workflow-card-dropdown-button-${id}`}
              size="xs"
            />
            <DropdownMenu>
              {isManageAccessEnabled && (
                <DropdownItem
                  onClick={() =>
                    dispatch(openManageAccess({ workflowInstanceId: id }))
                  }
                  data-testid={`workflow-card-manage-access-${id}`}
                >
                  <Flex gap="sm" align="center">
                    <RiGroupLine />
                    <span>
                      {t("dropdown.manageAccess", {
                        defaultValue: "Manage access",
                      })}
                    </span>
                  </Flex>
                </DropdownItem>
              )}
              {isRenameEnabled && (
                <DropdownItem data-testid={`workflow-card-rename-${id}`}>
                  <Flex gap="sm" align="center">
                    <RiEditLine />
                    <span>
                      {t("dropdown.rename", { defaultValue: "Rename" })}
                    </span>
                  </Flex>
                </DropdownItem>
              )}
              {isArchiveEnabled && (
                <DropdownItem data-testid={`workflow-card-archive-${id}`}>
                  <Flex gap="sm" align="center" className="text-error">
                    <RiArchiveLine />
                    <span>
                      {t("dropdown.archive", { defaultValue: "Archive" })}
                    </span>
                  </Flex>
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        )}
      </Flex>
      <Flex gap="sm" align="center">
        <span
          className="text-mono-dark"
          data-testid={`workflow-card-definition-${id}`}
        >
          {workflowDefinitionName}
        </span>
        <span className="text-mono-dark text-sm italic">v{version}</span>
      </Flex>

      <Flex className="mt-lg" gap="xs" align="center">
        <RiCornerDownRightLine className="text-2xl text-mono-light" />
        {stepInstance?.status === "InProgress" && !isAwaitingAttention && (
          <RiSettings2Line
            className="text-xl text-mono animate-spin [animation-duration:5s]"
            data-testid={`workflow-card-status-in-progress-${id}`}
          />
        )}
        {stepInstance?.status === "Completed" && (
          <RiCheckboxCircleFill
            className="text-xl text-success"
            data-testid={`workflow-card-status-completed-${id}`}
          />
        )}
        {stepInstance?.status === "Failed" && (
          <RiErrorWarningLine
            className="text-xl text-error"
            data-testid={`workflow-card-status-failed-${id}`}
          />
        )}
        {isAwaitingAttention && (
          <AlertBadge
            data-testid={`workflow-card-status-requires-attention-${id}`}
          />
        )}
        <span
          className={
            stepInstance?.status === "Failed" ? "text-error" : "text-mono-dark"
          }
          data-testid={`workflow-card-status-${id}`}
        >
          {stepInstance?.status === "Failed"
            ? t("workflowFailedText", { defaultValue: "Error occurred" })
            : stepInstance?.stepName}
        </span>
      </Flex>

      <Flex className="mt-lg" justify="between" align="end" gap="xs">
        <Link
          to={`/workflow/${id}`}
          state={{
            from: { pathname: location.pathname, search: location.search },
          }}
          className="card-link flex gap-xs text-link whitespace-nowrap items-center"
          data-testid={`workflow-card-view-link-${id}`}
          onMouseEnter={handlePrefetch}
        >
          {t("viewButton")}
          <RiArrowRightLongLine className="icon" size={16} />
        </Link>
        <span
          className="text-mono-dark"
          data-testid={`workflow-card-modified-on-${id}`}
        >
          {t("updated", { date: parseRelativeDate(modifiedOn) })}
        </span>
      </Flex>
    </div>
  );
}
